// ============================================================================
// AUDIT CORRECTIONS SERVICE (2026-10-14)
// ============================================================================
// crs.Api.Controllers.NightAuditModule.AuditCorrectionsController -> /api/v1/AuditCorrections

import { get, post } from '../client/axiosClient';
import { buildEndpoint, buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse } from '../types';
import type {
  AuditCorrectionResult,
  AuditCorrections,
  RecordMissedCheckInRequest,
} from '../types/auditCorrection.types';

const BASE = '/AuditCorrections';

const unwrap = <T,>(res: { data?: ApiResponse<T> }, fallback: string): T => {
  if (res.data?.data === undefined || res.data?.data === null) throw new Error(res.data?.message || fallback);
  return res.data.data;
};

class AuditCorrectionService {
  /** Unresolved past arrivals and the recent corrections (view). */
  async get(propertyId: string): Promise<AuditCorrections> {
    return unwrap(await get<ApiResponse<AuditCorrections>>(buildEndpointWithQuery(BASE, { propertyId })),
      'Audit corrections unavailable.');
  }

  /** Records a missed check-in on its real (closed) date and posts the missed nights (checkin). */
  async recordMissedCheckIn(bookingId: string, request: RecordMissedCheckInRequest): Promise<AuditCorrectionResult> {
    return unwrap(await post<ApiResponse<AuditCorrectionResult>>(buildEndpoint(`${BASE}/${bookingId}/check-in`), request),
      'The check-in was not recorded.');
  }

  /** Marks a booking whose arrival date is closed as no-show (no-show). */
  async markNoShow(bookingId: string, reason: string): Promise<AuditCorrectionResult> {
    return unwrap(await post<ApiResponse<AuditCorrectionResult>>(buildEndpoint(`${BASE}/${bookingId}/no-show`), { reason }),
      'The no-show was not recorded.');
  }

  /** Posts any closed night of the stay still not posted — the retry after a failure (checkin). */
  async postMissedNights(bookingId: string): Promise<AuditCorrectionResult> {
    return unwrap(await post<ApiResponse<AuditCorrectionResult>>(buildEndpoint(`${BASE}/${bookingId}/post-nights`), {}),
      'The nights were not posted.');
  }
}

export const auditCorrectionService = new AuditCorrectionService();
export default auditCorrectionService;
